/**
 * GDJ Favorites — wishlist kept in localStorage and, for a signed-in customer,
 * in the customer account (metafield gdj.favorites) so it follows them on every
 * device. The header renders window.gdjFavAccount = { customerId, items, endpoint }
 * (items = the account list); changes go to the app proxy endpoint
 * (/apps/ai/favorites -> gdj-api favorites.py, signed by Shopify).
 *
 * Signed in : the account is the reference. The first signed-in visit on a
 *             browser adds what was saved as a guest to the account. Changes are
 *             queued (gdj_favorites_pending) until the account confirms them.
 * Signed out: localStorage only. After a sign-out the browser copy is cleared
 *             (the list stays in the account).
 *
 * API:
 *   gdjFavorites.toggle(productId, productHandle)
 *   gdjFavorites.has(productId)
 *   gdjFavorites.getAll()        -> [{id, handle, addedAt}]
 *   gdjFavorites.count()
 *   gdjFavorites.remove(productId)
 *   gdjFavorites.clear()
 *
 * UI hooks:
 *   [data-fav-btn="<id>"] [data-fav-handle="<handle>"]  — toggle buttons
 *   [data-fav-count]                                     — count badges
 *   event 'gdj:favorites-change' { detail: { count } }
 */
(function () {
  // Guard against double-initialisation (the script may be referenced by more
  // than one section on a page). Without this, each copy adds its own click
  // listener and a single click toggles the favourite multiple times.
  if (window.gdjFavorites) return;

  const STORAGE_KEY = 'gdj_favorites';
  const OWNER_KEY = 'gdj_favorites_owner';     // customer id the local list mirrors
  const PENDING_KEY = 'gdj_favorites_pending'; // changes not yet confirmed by the account

  const account = window.gdjFavAccount || {};
  const customerId = account.customerId != null ? String(account.customerId) : '';
  const endpoint = account.endpoint || '/apps/ai/favorites';

  function read(key, fallback) {
    try {
      const v = JSON.parse(localStorage.getItem(key));
      return v == null ? fallback : v;
    } catch {
      return fallback;
    }
  }

  function write(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {}
  }

  function drop(key) {
    try {
      localStorage.removeItem(key);
    } catch {}
  }

  function normalize(f) {
    if (!f || f.id == null || String(f.id) === '') return null;
    return { id: String(f.id), handle: f.handle || '', addedAt: f.addedAt || '' };
  }

  function normalizeList(list) {
    return Array.isArray(list) ? list.map(normalize).filter(Boolean) : [];
  }

  function load() {
    return normalizeList(read(STORAGE_KEY, []));
  }

  function pending() {
    const p = read(PENDING_KEY, []);
    return Array.isArray(p) ? p : [];
  }

  function save(items) {
    write(STORAGE_KEY, items);
    document.dispatchEvent(new CustomEvent('gdj:favorites-change', { detail: { count: items.length } }));
    updateAllButtons();
    updateCountBadges();
  }

  function sameList(a, b) {
    return a.length === b.length && a.every((f, i) => f.id === b[i].id);
  }

  // Same semantics as the server: add appends unknown ids, remove drops ids.
  function applyOp(list, op) {
    const ids = new Set(op.items.map((i) => String(i.id)));
    if (op.op === 'remove') return list.filter((f) => !ids.has(String(f.id)));
    const have = new Set(list.map((f) => String(f.id)));
    return list.concat(op.items.filter((i) => !have.has(String(i.id))));
  }

  function withPending(accountItems) {
    return pending().reduce(applyOp, normalizeList(accountItems));
  }

  function queue(op) {
    write(PENDING_KEY, pending().concat([op]));
    flush();
  }

  // Send queued changes in order. Each answer is the account list; the local
  // copy becomes that list plus whatever is still queued. A failure keeps the
  // queue for the next change or page load.
  let flushing = false;
  async function flush() {
    if (!customerId || flushing) return;
    flushing = true;
    try {
      let q = pending();
      while (q.length) {
        const op = q[0];
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ op: op.op, items: op.items }),
        });
        if (!res.ok) {
          // 401 (signed out) / 429 / 5xx: keep the queue. Any other refusal is
          // about this change itself: drop it so it can't block the next ones.
          if (res.status === 401 || res.status === 429 || res.status >= 500) break;
          q = pending().filter((p) => p.seq !== op.seq);
          write(PENDING_KEY, q);
          continue;
        }
        const data = await res.json();
        q = pending().filter((p) => p.seq !== op.seq);
        write(PENDING_KEY, q);
        if (Array.isArray(data.items)) {
          const merged = withPending(data.items);
          if (!sameList(merged, load())) save(merged);
        }
      }
    } catch {
      // offline or proxy unavailable: retried later
    } finally {
      flushing = false;
    }
  }

  // Once per browser session, compare with the account itself (covers a page
  // rendered from a stale copy).
  async function verifyOnce() {
    const flag = 'gdj_favorites_checked_' + customerId;
    try {
      if (sessionStorage.getItem(flag)) return;
    } catch {}
    try {
      const res = await fetch(endpoint, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      if (!res.ok) return;
      const data = await res.json();
      if (!Array.isArray(data.items)) return;
      try {
        sessionStorage.setItem(flag, '1');
      } catch {}
      const merged = withPending(data.items);
      if (!sameList(merged, load())) save(merged);
    } catch {}
  }

  function syncWithAccount() {
    const owner = String(read(OWNER_KEY, '') || '');
    if (!customerId) {
      if (owner) {
        drop(OWNER_KEY);
        drop(PENDING_KEY);
        write(STORAGE_KEY, []);
      }
      return;
    }
    const accountItems = normalizeList(account.items);
    if (owner !== customerId) {
      // First signed-in visit on this browser: what was saved as a guest goes
      // into the account (another customer's leftovers never do).
      const have = new Set(accountItems.map((f) => f.id));
      const extra = owner ? [] : load().filter((f) => !have.has(f.id));
      write(PENDING_KEY, extra.length ? [{ seq: Date.now(), op: 'add', items: extra }] : []);
      write(OWNER_KEY, customerId);
    }
    write(STORAGE_KEY, withPending(accountItems));
    if (pending().length) flush();
    else verifyOnce();
  }

  function has(productId) {
    return load().some((f) => String(f.id) === String(productId));
  }

  function change(op) {
    save(applyOp(load(), op));
    if (customerId) queue(op);
  }

  function newOp(kind, items) {
    return { seq: Date.now() + Math.random(), op: kind, items };
  }

  function toggle(productId, productHandle) {
    const id = String(productId);
    const added = !has(id);
    change(newOp(added ? 'add' : 'remove', [{ id, handle: productHandle || '', addedAt: new Date().toISOString() }]));

    if (window.gdjToast) {
      window.gdjToast(added ? 'Added to favorites' : 'Removed from favorites');
    }

    return added;
  }

  function remove(productId) {
    if (has(productId)) change(newOp('remove', [{ id: String(productId) }]));
  }

  function getAll() {
    return load();
  }

  function count() {
    return load().length;
  }

  function clear() {
    const items = load();
    if (items.length) change(newOp('remove', items.map((f) => ({ id: f.id }))));
  }

  function updateAllButtons() {
    document.querySelectorAll('[data-fav-btn]').forEach((btn) => {
      const id = btn.getAttribute('data-fav-btn');
      const active = has(id);
      btn.classList.toggle('gdj-fav--active', active);
      btn.setAttribute('aria-pressed', String(active));
      // Reflect the state on the card's "Favori" pill too.
      const card = btn.closest('.gdj-card');
      const pill = card && card.querySelector('[data-gdj-favpill]');
      if (pill) pill.hidden = !active;
    });
  }

  function updateCountBadges() {
    const c = count();
    document.querySelectorAll('[data-fav-count]').forEach((el) => {
      el.textContent = c;
      el.hidden = c === 0;
    });
  }

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-fav-btn]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    toggle(btn.getAttribute('data-fav-btn'), btn.getAttribute('data-fav-handle') || '');
  });

  syncWithAccount();

  function init() {
    updateAllButtons();
    updateCountBadges();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Cards are re-rendered dynamically (Algolia, carousels, favourites page),
  // so re-apply the active heart + pill state whenever the DOM changes.
  let reapplyTimer;
  const observeRoot = document.querySelector('#MainContent') || document.body;
  new MutationObserver(() => {
    clearTimeout(reapplyTimer);
    reapplyTimer = setTimeout(updateAllButtons, 150);
  }).observe(observeRoot, { childList: true, subtree: true });

  window.gdjFavorites = { toggle, has, getAll, count, remove, clear };
})();
